<!-- Today's Street. Twenty-four doors for 1–24 December, the same seeded
     street for everyone. Outside December: a countdown and Free Play. -->
<template>
  <IonPage>
    <AppHeader title="Today's Street" back="/" />

    <IonContent :fullscreen="true">
      <div class="soc-stack calendar-stack">
        <div v-if="today === null" class="soc-card calendar-countdown">
          <p class="soc-muted ion-text-center">{{ countdownLine }}</p>
          <p class="ion-text-center">The first door opens on 1 December. Free Play is open now.</p>
          <IonButton class="soc-play" @click="freePlay">Play</IonButton>
        </div>

        <template v-else>
          <p class="soc-muted ion-text-center">Same street for everyone. Past days stay open.</p>

          <div class="doors" role="list">
            <button
              v-for="day in days"
              :key="day"
              type="button"
              role="listitem"
              class="door"
              :class="{ 'door--today': day === today, 'door--locked': !unlocked(day), 'door--done': unlocked(day) && bestFor(day) > 0 }"
              :aria-disabled="!unlocked(day)"
              :aria-label="doorLabel(day)"
              @click="openDay(day)"
            >
              <span class="door__num">{{ day }}</span>
              <IonIcon v-if="!unlocked(day)" :icon="lockClosedOutline" aria-hidden="true" />
              <span class="door__sub">{{ doorSub(day) }}</span>
            </button>
          </div>
        </template>
      </div>
    </IonContent>
  </IonPage>
</template>

<script setup>
import { computed } from 'vue';
import { IonPage, IonContent, IonButton, IonIcon } from '@ionic/vue';
import { lockClosedOutline } from 'ionicons/icons';
import { useRouter } from 'vue-router';
import { AppHeader } from '../core/ui';
import { progress, formatTotal } from '../store.js';
import { dayIndexFor, isDayUnlocked, FIRST_DAY, LAST_DAY, CAMPAIGN_YEAR } from '../lib/daily.js';

const router = useRouter();

const today = computed(() => dayIndexFor(new Date()));
const days = Array.from({ length: LAST_DAY - FIRST_DAY + 1 }, (_, i) => i + FIRST_DAY);

const countdownLine = computed(() => {
  const now = new Date();
  const dec1 = new Date(Date.UTC(CAMPAIGN_YEAR, 11, 1, -11)); // 1 Dec, Sydney morning
  const daysLeft = Math.max(0, Math.ceil((dec1 - now) / 86400000));
  if (daysLeft <= 0) return 'Opens 1 December';
  return `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} to 1 December`;
});

function unlocked(day) {
  return isDayUnlocked(day);
}

function bestFor(day) {
  return Number(progress.bestByDay[String(day)]) || 0;
}

function doorSub(day) {
  if (!unlocked(day)) return `Opens ${day} Dec`;
  const best = bestFor(day);
  return best > 0 ? `Best ${formatTotal(best)}` : 'Play';
}

function doorLabel(day) {
  if (!unlocked(day)) return `Day ${day}, opens ${day} December`;
  const best = bestFor(day);
  return best > 0 ? `Day ${day}, best score ${formatTotal(best)}` : `Day ${day}, play`;
}

function openDay(day) {
  if (!unlocked(day)) return;
  router.push({ name: 'play', params: { mode: 'day', code: String(day) } });
}

function freePlay() {
  router.push({ name: 'play', params: { mode: 'free' } });
}
</script>

<style scoped>
.calendar-stack {
  padding-top: var(--sp-4);
}

.calendar-countdown {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
  text-align: center;
}

.calendar-countdown p {
  margin: 0;
}
</style>
